/**
 * The visit, kept for analysis: answers, the photo, and the avatar we drew.
 *
 * Mixpanel counts. This keeps the material — the two images side by side are
 * the only way to see whether a disappointing character was a bad crop, a bad
 * reading of the photo or a bad prompt, and the answers next to them say who it
 * was for. Both funnels write into the same table, keyed by the same id, so a
 * parent who walks one and then the other is two rows that can be joined rather
 * than two systems that cannot.
 *
 * It is deliberately invisible. Every call is fire-and-forget: a failed request
 * is swallowed, a server without Supabase configured answers 501 and is never
 * asked again, and nothing in the funnel ever waits on a reply. If this file
 * stopped working, the only symptom would be an empty table.
 *
 * `sendBeacon` is not used: it is capped at 64 KB, and a photo is not. Nor is
 * `keepalive` on the image posts — it shares the same 64 KB cap, and Chrome
 * drops a larger body on the floor without a word. The images go while the
 * page is very much still there (the analysis screen), so they need no help
 * outliving it; only the small session writes ride with keepalive.
 */

const ENDPOINT = '/__collect';
const KEY = 'ct.collect.id';

export type Funnel = 'onboarding' | 'bible';

/** What a browser is allowed to set — the server keeps its own allow-list too. */
export interface SessionFields {
  distinct_id?: string;
  child_name?: string;
  answers?: Record<string, string | string[]>;
  look?: unknown;
  analysis?: unknown;
  plan?: string;
  furthest_screen?: string;
  screens_seen?: number;
  reached_paywall?: boolean;
  checkout_started?: boolean;
  purchased?: boolean;
  purchased_at?: string;
  test_mode?: boolean;
  resumed?: boolean;
  demo?: boolean;
  utm?: Record<string, string>;
  referrer?: string;
  landing_path?: string;
  design?: string;
  locale?: string;
  viewport?: string;
}

/**
 * One id per visit, in sessionStorage.
 *
 * sessionStorage and not localStorage: a row is a visit, not a person. The same
 * parent coming back next week is a second row, which is what makes "did the
 * second attempt go better" answerable. It is shared across the two funnels in
 * one tab on purpose — walking from /bible to /onboarding is one visit.
 */
export function visitId(): string {
  try {
    const found = sessionStorage.getItem(KEY);
    if (found) return found;
    const made = crypto.randomUUID();
    sessionStorage.setItem(KEY, made);
    return made;
  } catch {
    // Private mode: an id that lives as long as the page is still better than
    // no row at all, it just cannot survive a reload.
    return (memoryId ||= crypto.randomUUID());
  }
}
let memoryId = '';

/** Off for good once the server says it has nowhere to put this. */
let off = false;

function post(payload: Record<string, unknown>, keepalive = true): void {
  if (off) return;
  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive,
  })
    .then((r) => { if (r.status === 501) off = true; })
    .catch(() => { /* the funnel does not depend on this */ });
}

/** Where this visit came from, as far as the URL admits. */
function arrival(): SessionFields {
  const q = new URLSearchParams(location.search);
  const utm: Record<string, string> = {};
  for (const [k, v] of q) if (k.startsWith('utm_') || k === 'fbclid' || k === 'gclid' || k === 'ttclid') utm[k] = v;
  return {
    utm: Object.keys(utm).length ? utm : undefined,
    referrer: document.referrer || undefined,
    landing_path: location.pathname,
    design: q.get('design') || 'v3',
    locale: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
}

/**
 * The answers change on nearly every tap, and the row is small, so writes are
 * coalesced: the last state within a beat wins and one request carries it.
 */
let pending: SessionFields = {};
let timer: ReturnType<typeof setTimeout> | null = null;
let sentArrival = false;

export function session(funnel: Funnel, fields: SessionFields): void {
  if (off) return;
  if (!sentArrival) { pending = { ...arrival(), ...pending }; sentArrival = true; }
  pending = { ...pending, ...fields };
  if (timer) return;
  timer = setTimeout(() => {
    const fieldsNow = pending;
    pending = {};
    timer = null;
    post({ id: visitId(), funnel, kind: 'session', fields: fieldsNow });
  }, 500);
}

/** The photo as the parent uploaded it, once, at the moment they move on. */
export function photo(funnel: Funnel, dataUrl: string | null): void {
  if (!dataUrl) return;
  post({ id: visitId(), funnel, kind: 'photo', image: dataUrl }, false);
}

/** The character we drew from it — the other half of every comparison. */
export function avatar(funnel: Funnel, dataUrl: string | null): void {
  if (!dataUrl) return;
  post({ id: visitId(), funnel, kind: 'avatar', image: dataUrl }, false);
}

export const collect = { visitId, session, photo, avatar };
